import { createAdkSessionAdapter, createAdkStream, useAdkRuntime } from '@assistant-ui/react-google-adk';
import { useMemo } from 'react';
import { queryClient } from '@/app/queryClient';
import { sessionHeaders } from '@/lib/api/client';
import { newCorrelationId, reportClient } from '@/lib/log/clientLog';
import { useErrorStore } from '@/store/useErrorStore';
import { SESSION_PAGE_SIZE, listMySessions, setSessionArchived, setSessionTitle, type AgentSession } from './api';
import { simonAttachments } from './attachments';
import { sessionKeys } from './queries';

/** The ADK REST surface the BFF presents (decision 2026-10-06: direct mode, not proxy). */
export const ADK_API = '/bff/adk';
export const APP_NAME = 'simon';

// Archived conversations shown in the sidebar's Archived section (first page).
const ARCHIVED_LIMIT = 50;

function toThread(s: AgentSession, status: 'regular' | 'archived') {
  return {
    status,
    remoteId: s.vertexSessionId,
    externalId: s.vertexSessionId,
    title: s.title || s.firstUserMessage || undefined,
    lastMessageAt: s.lastActivityAt ? new Date(s.lastActivityAt) : undefined,
  };
}

/**
 * One page of the sidebar: 10 active conversations (cursor = offset), plus —
 * with the first page only — the archived ones for the Archived section.
 */
export async function listThreadsPage(params?: { after?: string }) {
  const offset = params?.after ? Number(params.after) : 0;
  const [active, archived] = await Promise.all([
    listMySessions({ archived: false, limit: SESSION_PAGE_SIZE, offset }),
    offset === 0 ? listMySessions({ archived: true, limit: ARCHIVED_LIMIT }) : Promise.resolve([]),
  ]);
  return {
    threads: [...active.map((s) => toThread(s, 'regular')), ...archived.map((s) => toThread(s, 'archived'))],
    nextCursor: active.length === SESSION_PAGE_SIZE ? String(offset + SESSION_PAGE_SIZE) : undefined,
  };
}

/**
 * assistant-ui runtime for Simon.
 *
 *   - stream, load and create go to the BFF's ADK surface; the session cookie
 *     rides along (same origin) and X-CSRF-Token is added per request
 *   - the thread LIST, archive and rename go to sreg-api: it pages (10 at a
 *     time, cursor = offset) and carries title / first-message preview
 *   - there is no delete — conversations are archived, never removed
 *
 * userId must be the signed-in user's id (the session `sid`); the BFF refuses any other.
 */
export function useSimonRuntime(userId: string) {
  const { stream, sessions, load } = useMemo(() => {
    // Each chat call gets its own CorrelationID (logged by the BFF, sreg-api and the
    // agent). The latest is kept so a chat error can show it.
    const headers = () => {
      lastChatCorrelationId = newCorrelationId();
      return { ...sessionHeaders(), CorrelationID: lastChatCorrelationId };
    };
    const { adapter, load } = createAdkSessionAdapter({ apiUrl: ADK_API, appName: APP_NAME, userId, headers });
    const changed = () => void queryClient.invalidateQueries({ queryKey: sessionKeys.all });

    const sessions = {
      ...adapter,
      // assistant-ui swallows a failed create; surface it (e.g. a 403 from the BFF).
      async initialize(threadId: string) {
        try {
          return await adapter.initialize(threadId);
        } catch (err) {
          showChatError('Simon could not start a new conversation.', err);
          throw err;
        }
      },
      list: listThreadsPage,
      async archive(remoteId: string) {
        await setSessionArchived(remoteId, true);
        changed();
      },
      async unarchive(remoteId: string) {
        await setSessionArchived(remoteId, false);
        changed();
      },
      async rename(remoteId: string, title: string) {
        await setSessionTitle(remoteId, title);
        changed();
      },
      async delete() {
        throw new Error('Conversations are archived, not deleted.');
      },
    };
    return { stream: createAdkStream({ api: ADK_API, appName: APP_NAME, userId, headers }), sessions, load };
  }, [userId]);

  return useAdkRuntime({
    stream,
    sessionAdapter: sessions,
    load,
    adapters: { attachments: simonAttachments },
    eventHandlers: { onError: (err: unknown) => showChatError('Simon could not finish that answer.', err) },
  });
}

/** The CorrelationID of the most recent chat call (shown with chat errors). */
let lastChatCorrelationId = '';

/** What the BFF sends when Gemini is out of capacity (429 / RESOURCE_EXHAUSTED). */
export const LLM_EXHAUSTED = 'LLM Resources Exhausted';

/** The toast text for a failed answer. */
export function chatErrorMessage(message: string, detail: string): string {
  if (/LLM Resources Exhausted|RESOURCE_EXHAUSTED|\b429\b/.test(detail)) return LLM_EXHAUSTED;
  const status = /\b(401|403|404|5\d\d)\b/.exec(detail)?.[1];
  return status === '401' || status === '403' ? `${message} Your session may have expired — sign in again.` : message;
}

function showChatError(message: string, err: unknown) {
  // The ADK client hands over a bare string for in-stream errors: wrap it so
  // the error report carries its text instead of "NonError".
  const error = err instanceof Error ? err : new Error(typeof err === 'string' ? err : String(err));
  const detail = error.message;
  const correlationId = lastChatCorrelationId || undefined;
  reportClient({ source: 'chat.stream', level: 'ERROR', message: `${message} ${detail}`.trim(), error, correlationId });
  useErrorStore.getState().show({ message: chatErrorMessage(message, detail), correlationId });
}
